/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {Module, Node} from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import MsDyn365, {RichTextComponent} from '@msdyn365-commerce/core';

import {
    ISignUpEmailVerification,
    ISignUpGivenNameVerification,
    ISignUpItem,
    ISignUpLoading,
    ISignUpLocalAccount,
    ISignUpSurnameVerification,
    ISignUpViewProps
} from '@msdyn365-commerce-modules/account-management/src/modules/sign-up/./sign-up';
import {ISignUpConfig, ISignUpResources} from '../definition-extensions/sign-up.ext.props.autogenerated';
/*
// can't use because of sign-in default module and consecutive error in pipeline
declare global {
    interface Window {
        SA_FIELDS?:any;
    }
}*/

export interface ISignUpViewPropsExt extends ISignUpViewProps {
    resources: ISignUpResources;
    config: ISignUpConfig;


}
export interface ISignUpLocalAccountExt extends ISignUpLocalAccount {
    resources: ISignUpResources;
    config: ISignUpConfig;
}
const SignUpItem: React.FC<ISignUpItem> = ({
    wrapper,
    label,
    errorMessage,
    input
}) => {

    return (
        <Node {...wrapper}>
            {label}
            {errorMessage}
            {input}
        </Node>
    );
};

const SignUpEmailVerification: React.FC<ISignUpEmailVerification> = ({
    email,
    buttonWrapper,
    buttons,
    successMessage,
    errorMessage
}) => {

    return (
        <>
            <SignUpItem {...email} />
            {successMessage && successMessage.map((message: React.ReactNode) => {
                return (
                    <>
                        {message}
                    </>
                );
            })}
            {errorMessage && errorMessage.map((message: React.ReactNode) => {
                return (
                    <>
                        {message}
                    </>
                );
            })}
            <Node {...buttonWrapper}>
                {buttons && buttons.map((button: React.ReactNode) => {
                    return (
                        <>
                            {button}
                        </>
                    );
                })}
            </Node>
        </>
    );
};

/**
 * Handles validation of sign-up givenName.
 * @param event - Content editable event.
 * @param event.givenName - Event Given Name.
 * @param event.errorMessage - Event Error Message.
 * @returns - Error Message for Given Name.
 */
const SignUpGivenNameVerificationComponent: React.FC<ISignUpGivenNameVerification> = ({
    givenName,
    errorMessage
}) => {
    return (
        <>
            <SignUpItem {...givenName} />
            {errorMessage.map((message: React.ReactNode) => {
                return (
                    <>
                        {message}
                    </>
                );
            })}
        </>
    );
};

/**
 * Handles validation of signUp surname.
 * @param event - Content editable event.
 * @param event.surname - Event surname.
 * @param event.errorMessage - Event Error Message.
 * @returns - Error Message for surname.
 */
const SignUpSurnameVerificationComponent: React.FC<ISignUpSurnameVerification> = ({
    surname,
    errorMessage
}) => {
    return (
        <>
            <SignUpItem {...surname} />
            {errorMessage.map((message: React.ReactNode) => {
                return (
                    <>
                        {message}
                    </>
                );
            })}
        </>
    );
};


const LocalAccount: React.FC<ISignUpLocalAccountExt> = ({
                                                            localAccount,
                                                            items,
                                                            emailVerification,
                                                            givenNameVerification,
                                                            surnameVerification,
                                                            buttons,
                                                            errorMessage,
                                                            disclaimer,
                                                            resources,
                                                            config
                                                        }) => {
    return (
        <Node {...localAccount}>
            {items && items.map((item: ISignUpItem) => {
                if (item.key === 'email' && emailVerification.isRequired) {
                    return (
                        <Node {...emailVerification.verificationControlWrapper}>
                            <SignUpItem {...item} />
                            <SignUpEmailVerification {...emailVerification} />
                        </Node>
                    );
                }
                if (item.key === 'givenName' && givenNameVerification.isRequired) {
                    return (
                        <Node {...givenNameVerification.verificationControlWrapper}>
                            <SignUpItem {...item} />
                            <SignUpGivenNameVerificationComponent {...givenNameVerification} />
                        </Node>
                    );
                }
                if (item.key === 'surname' && surnameVerification.isRequired) {
                    return (
                        <Node {...surnameVerification.verificationControlWrapper}>
                            <SignUpItem {...item} />
                            <SignUpSurnameVerificationComponent {...surnameVerification} />
                        </Node>
                    );
                }
                return (

                    <SignUpItem {...item} />

                );
            })}

            <SelectLanguage />

            <Consent resources={resources}
            config={config}/>

            {errorMessage && errorMessage.map((error: React.ReactNode, index: number) => {
                return (
                    <React.Fragment key={index}>
                        {error}
                    </React.Fragment>
                );
            })}


            {buttons && buttons.map((button: React.ReactNode, index: number) => {
                return (
                    <React.Fragment key={index}>
                        {button}
                    </React.Fragment>
                );
            })}
            {disclaimer}
        </Node>
    );
};


const SignUpLoading: React.FC<ISignUpLoading> = ({
    modal,
    modalBody,
    icon,
    message
}) => {
    return (
        <Node {...modal}>
            <Node {...modalBody}>
                {icon}
                {message}
            </Node>
        </Node>
    );
};

const SignUpView: React.FC<ISignUpViewPropsExt> = props => {
    const {
        signUp,
        viewState,
        loading,
        defaultAADConainer,
        aadConainer,
        heading,
        signUpLocalAccount,
        resources,
        config
    } = props;

    const signUpLocalAccountExt = {
        ...signUpLocalAccount,
        resources: resources,
        config:config
    }
    return (
        <Module {...signUp}>
            {viewState.isShowLoading && <Node {...defaultAADConainer} />}
            {viewState.isShowLoading && <SignUpLoading {...loading} />}
            <Node {...aadConainer}>
                {heading}
                {LocalAccount(signUpLocalAccountExt)}
            </Node>
        </Module>
    );
};

function CheckItem(props:any) {
    return (

      <div className='checkitem' key={props.id}>
       <div className={'checkitem__title'}>
          {props.text} {props.required && '*'}
       </div>
       <div className='checkboxrow'>
           <label className="type--checkbox" htmlFor={props.name}>
               <input required={true} type='checkbox' aria-checked='false' defaultChecked={false} name={props.name} value={'true'}/>
               <RichTextComponent
                   className="consentlabel"
                   text={props.config}
               />
           </label>
        </div>
      </div>
    );
};


function Consent (props:{resources:ISignUpResources,config: ISignUpConfig }){
    const handleChange = (event:React.ChangeEvent<HTMLInputElement>) => {
        setConsents({
            ...consents,
            [event.target.name]: event.target.checked
        });
    }

    const [consents, setConsents] = React.useState({
      extension_Privacyagreement: false,
      extension_Marketingconsent: false,
      extension_Profilingconsent: false
    });

    const consentsData = [
        {
            name:"extension_Privacyagreement",
            text:props.resources.extension_Privacyagreement,
            required: true,
            onChange : handleChange
        },
        {
            name:"extension_Marketingconsent",
            text:props.resources.extension_Marketingconsent,
            required: false,
            onChange: handleChange
        },
        {
            name:"extension_Profilingconsent",
            text:props.resources.extension_Profilingconsent,
            required: false,
            onChange : handleChange
        }];

    const renderCheck = consentsData.map((consent) => {
        return (
            <CheckItem name={consent.name}
                      text={consent.text}
                      id={consent.name}
                      checked={consents[consent.name]}
                      onChange={consent.onChange}
                      config={props.config[consent.name]}
                      required={consent.required}
            />
        )
    });

    return(
        <>
            {renderCheck}
        </>
    )
}

interface ILangOption {
    AUTOFOCUS?: boolean;
    DISP?: string;
    PRESEL?: boolean;
    VAL?: string;
}

const SelectLanguage = () => {
    // @ts-ignore
    const langSelectorField = MsDyn365.isBrowser && window.SA_FIELDS && window.SA_FIELDS.AttributeFields && window.SA_FIELDS.AttributeFields.find((field: { ID: string; }) => field.ID === 'extension_Language');

    if(langSelectorField){
        const handleChange = (event: { target: { value: React.SetStateAction<string>; }; }) => {
            setLang(event.target.value);
        }
        const options = langSelectorField.OPTIONS && langSelectorField.OPTIONS;
        const [lang, setLang] = React.useState(options[0].VAL);

        return (
            <div className={'ms-sign-up__account-item ms-sign-up__account-item-lang'}>
                <label>{langSelectorField.DN}</label>
                <select value={lang} id={'extension_Language_f'}
                        name={'extension_Language_f'} onChange={handleChange} className={'ms-sign-up__dropdown'}>
                    {options.map((option:ILangOption) => (
                        <option key={option.VAL} value={option.VAL} selected={lang == option.VAL}>
                            {option.DISP}
                        </option>
                    ))}
                </select>
                <input style={{'display':'none'}}  name={'extension_Language'}  id={'extension_Language'} value={lang} />
            </div>
        )
    } else {
        return (
            <div className={'ms-sign-up__account-item ms-sign-up__account-item-lang'} />
        );
    }
}

export default SignUpView;
